import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ModalImage from 'react-modal-image';
import './businessCard.css';

const BusinessCard = ({ biz }) => {
  const {
    _id,
    name = 'Unnamed Business',
    logo,
    photo,
    description = 'No description provided.',
    address = 'No address listed.',
    website,
    contactEmail,
    contactPhone,
    region = 'Unknown region',
    tags = [],
    isActive = true,
    hasClue,
    location,
    checkIns = [],
    owner,
    managers = [],
    createdAt,
    updatedAt,
  } = biz || {};

  const logoUrl =
    logo ||
    'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';

  const fallbackPhoto =
    'https://res.cloudinary.com/joegalaxy/image/upload/v1753589314/68246674-your-business-here-rubber-stamp_ra7bvr.jpg';
  const mainImage = photo || fallbackPhoto;

  const coordinates =
    location &&
    Array.isArray(location.coordinates) &&
    location.coordinates.length === 2
      ? location.coordinates
      : null;

  const managerCount = Array.isArray(managers) ? managers.length : 0;
  const isUnclaimed = !owner;

  return (
    <Card
      className={`business-card shadow-sm animate__animated animate__fadeInUp${
        isUnclaimed ? ' business-card-unclaimed' : ''
      }`}
    >
      {/* Header: Photo, Logo, Unclaimed badge */}
      <div
        className="business-card-img-wrapper"
        style={{ position: 'relative' }}
      >
        {/* Main Photo Preview */}
        <ModalImage
          small={mainImage}
          large={mainImage}
          alt={name + ' photo'}
          className="business-card-img"
          hideDownload
          hideZoom
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = fallbackPhoto;
          }}
        />
        {/* Logo Avatar */}
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 48,
            height: 48,
            borderRadius: '50%',
            overflow: 'hidden',
            boxShadow: '0 2px 12px #2563eb33',
            border: '2.5px solid #fff',
            background: '#fff',
            zIndex: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ModalImage
            small={logoUrl}
            large={logoUrl}
            alt={name + ' logo'}
            hideDownload
            hideZoom
            className="business-card-logo-avatar"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        {/* Unclaimed Badge */}
        {isUnclaimed && (
          <Badge
            bg="warning"
            className="business-card-unclaimed-badge"
            style={{
              position: 'absolute',
              top: 14,
              left: 14,
              zIndex: 5,
              fontWeight: 700,
              fontSize: '1.02em',
              padding: '0.45em 1.1em',
              borderRadius: '16px',
              boxShadow: '0 2px 8px #fbbf2433',
              letterSpacing: '0.5px',
            }}
          >
            Unclaimed
          </Badge>
        )}
      </div>
      {/* Main content: clickable area */}
      <Link
        to={`/business/${_id}`}
        className="business-card-link"
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <Card.Body>
          <Card.Title className="mb-1 d-flex align-items-center justify-content-between business-card-title">
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {name}
              {hasClue > 0 && (
                <Badge
                  bg="success"
                  className="ms-2 animate__animated animate__tada"
                  style={{
                    fontSize: '1em',
                    padding: '0.35em 0.8em',
                    borderRadius: '12px',
                    marginLeft: 10,
                  }}
                >
                  🧩 Clue
                </Badge>
              )}
              {!isActive && (
                <Badge
                  bg="secondary"
                  className="ms-2"
                  style={{
                    fontSize: '0.95em',
                    padding: '0.32em 0.7em',
                    borderRadius: '10px',
                    marginLeft: 8,
                  }}
                >
                  Inactive
                </Badge>
              )}
            </span>
          </Card.Title>
          <Card.Text className="business-card-desc">{description}</Card.Text>
          <div className="business-card-address">
            <small>📍 {address}</small>
          </div>
          {coordinates && (
            <div className="business-card-coords mt-1">
              <small>
                <span role="img" aria-label="location">
                  🗺️
                </span>{' '}
                {coordinates[1].toFixed(5)}, {coordinates[0].toFixed(5)}
              </small>
            </div>
          )}
          <div className="business-card-meta mt-2">
            {region && (
              <span
                className="badge bg-info text-dark me-1"
                style={{ fontWeight: 600 }}
              >
                {region.charAt(0).toUpperCase() + region.slice(1)}
              </span>
            )}
            {Array.isArray(tags) && tags.length > 0 && (
              <span style={{ display: 'inline-block', marginTop: 6 }}>
                {tags.slice(0, 3).map((tag, i) => (
                  <span
                    key={i}
                    className="badge bg-light text-secondary ms-1"
                    style={{
                      fontWeight: 500,
                      fontSize: '0.98em',
                      marginTop: 2,
                      marginBottom: 2,
                      marginLeft: i === 0 ? 8 : 4,
                    }}
                  >
                    #{tag}
                  </span>
                ))}
                {tags.length > 3 && (
                  <span
                    className="ms-1"
                    style={{ color: '#64748b', fontWeight: 500 }}
                  >
                    +{tags.length - 3} more
                  </span>
                )}
              </span>
            )}
          </div>
          <div
            className="business-card-timestamps mt-3"
            style={{ display: 'flex', gap: 12, alignItems: 'center' }}
          >
            <small
              style={{
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <span
                title="Created"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                🕒{' '}
                {createdAt ? new Date(createdAt).toLocaleDateString() : 'N/A'}
              </span>
            </small>
            <span style={{ color: '#cbd5e1', fontSize: 18 }}>•</span>
            <small
              style={{
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <span
                title="Updated"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                🔄{' '}
                {updatedAt ? new Date(updatedAt).toLocaleDateString() : 'N/A'}
              </span>
            </small>
          </div>
        </Card.Body>
      </Link>
      {/* Contact row: not in Link */}
      <div
        className="business-card-contact mt-2 px-3 pb-3"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {website && (
          <a
            href={website.startsWith('http') ? website : `https://${website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="me-2"
            title="Website"
            style={{ fontSize: 22, color: '#2563eb', transition: 'color 0.2s' }}
          >
            🌐
          </a>
        )}
        {contactEmail && (
          <a
            href={`mailto:${contactEmail}`}
            className="me-2"
            title="Email"
            style={{ fontSize: 22, color: '#16a34a', transition: 'color 0.2s' }}
          >
            📧
          </a>
        )}
        {contactPhone && (
          <a
            href={`tel:${contactPhone}`}
            className="me-2"
            title="Call"
            style={{ fontSize: 22, color: '#f59e42', transition: 'color 0.2s' }}
          >
            📞
          </a>
        )}
        {checkIns.length > 0 && (
          <span
            className="badge bg-warning text-dark ms-1"
            title="Check-ins"
            style={{ fontWeight: 600, fontSize: '0.98em' }}
          >
            {checkIns.length} Check-in{checkIns.length > 1 ? 's' : ''}
          </span>
        )}
        {managerCount > 0 && (
          <span
            className="badge bg-primary text-light ms-1"
            title="Managers"
            style={{ fontWeight: 600, fontSize: '0.98em' }}
          >
            {managerCount} Manager{managerCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </Card>
  );
};

export default BusinessCard;
